<template>
  <section class="wbs-step" aria-labelledby="wbs-heading" data-testid="website-step">
    <div class="wbs-heading">
      <h2 id="wbs-heading" class="wbs-title" data-testid="wbs-title">{{ title }}</h2>
      <p v-if="subtitle" class="wbs-subtitle" data-testid="wbs-subtitle">{{ subtitle }}</p>
    </div>

    <div class="wbs-content">
      <div class="wbs-field">
        <p id="wbs-label" class="wbs-label" data-testid="wbs-label">Company websites (optional)</p>
        <CompanyWebsitesField
          :websites="websiteList"
          :show-heading="false"
          wizard-style
          labelledby="wbs-label"
          @add="addWebsite"
          @remove="removeWebsite"
        />
      </div>
      <p class="wbs-note" data-testid="wbs-note">{{ noteText }}</p>
    </div>

    <div class="wbs-actions">
      <StandardButton
        type="magenta"
        variant="primary"
        class="wbs-action wbs-action--continue"
        :label="buttonLabel"
        :disabled="!canContinue"
        data-testid="wbs-continue"
        @click="handleContinue"
      />
      <StandardButton
        type="magenta"
        variant="text-secondary"
        class="wbs-action wbs-action--skip"
        label="Skip for now"
        data-testid="wbs-skip"
        @click="emit('skip')"
      />
      <StandardButton
        type="magenta"
        variant="text-secondary"
        class="wbs-action wbs-action--do-not-ask"
        label="Do not ask again"
        data-testid="wbs-do-not-ask"
        @click="emit('do-not-ask')"
      />
    </div>
  </section>
</template>

<script setup>
import { computed, ref } from "vue";
import CompanyWebsitesField from "@components/CompanyWebsitesField/CompanyWebsitesField.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";

/**
 * ER-12246 — WebsiteStep (MDP-621).
 *
 * "Add your company website" (page key `website`): enters websites with the shared
 * Company websites component of IQ Settings (ER-12778). Continue is disabled until at
 * least one website has been added; Skip for now and Do not ask again are always
 * available. Presentational — Continue emits the list and the flow controller saves
 * it (ER-12244).
 */
const props = defineProps({
  title: { type: String, default: "Add your company website" },
  subtitle: {
    type: String,
    default:
      "IQ reads your public websites to learn about your company and uses what it finds as context for its answers.",
  },
  noteText: {
    type: String,
    default:
      "IQ scans the websites and stores what it finds as account context. You can change them later in IQ settings.",
  },
  /** Websites already on the account, if any. */
  websites: { type: Array, default: () => [] },
  buttonLabel: { type: String, default: "Continue" },
});

const emit = defineEmits(["continue", "skip", "do-not-ask"]);

const websiteList = ref([...props.websites]);

const canContinue = computed(() => websiteList.value.length > 0);

function addWebsite(url) {
  if (!websiteList.value.includes(url)) websiteList.value.push(url);
}

function removeWebsite(url) {
  websiteList.value = websiteList.value.filter((website) => website !== url);
}

function handleContinue() {
  if (!canContinue.value) return;
  emit("continue", { websites: [...websiteList.value] });
}
</script>

<style scoped>
.wbs-step {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  height: 100%;
  padding: 0;
  background: #ffffff;
  color: #101828;
  font-family: Inter, sans-serif;
}

.wbs-heading {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.wbs-title {
  margin: 0;
  color: #101828;
  font-size: 36px;
  font-weight: 600;
  line-height: 44px;
}

.wbs-subtitle {
  margin: 0;
  color: #475467;
  font-size: 18px;
  line-height: 28px;
}

.wbs-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.wbs-field {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.wbs-label {
  margin: 0;
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.wbs-note {
  margin: 0;
  color: #475467;
  font-size: 14px;
  line-height: 20px;
}

.wbs-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.wbs-actions .wbs-action {
  box-sizing: border-box;
  padding: 0 16px;
  font-family: inherit;
  font-size: 16px;
  line-height: 24px;
}

.wbs-actions .wbs-action--continue,
.wbs-actions .wbs-action--skip {
  height: 44px;
}

.wbs-actions .wbs-action--continue {
  width: 100%;
}

.wbs-actions .wbs-action--do-not-ask {
  height: 24px;
  padding: 0;
}

.wbs-actions .wbs-action.standard-button--text-secondary.standard-button--default,
.wbs-actions .wbs-action.standard-button--text-secondary.standard-button--active {
  color: #475467;
}

.wbs-actions .wbs-action:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}
</style>
